import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as moment from 'moment';
import {Report} from '../models/report.model';
import {isDealPayrollIdentityMatch} from '../models/deal-payroll.model';
import {dealPayrollMonthFromRange} from '../services/report.service';
import {ReportService} from '../services/report.service';
import {HTTP_STATUS_CODES} from '../shared.constants/all-constants';
import {
  DATE_FORMAT,
  FIRST_REPORT_INDEX,
  REPORT_DATE_PREFIX,
  REPORT_DATE_SEPARATOR
} from './report-datepicker.constants';
import {ReportDatepickerDepartmentLoader} from './report-datepicker-department.loader';
import {applyReportDatepickerError} from './report-datepicker-error.state';
import {formatReportFields} from './report-datepicker-report.mapper';
import {ReportDatepickerReportPresenter} from './report-datepicker-report.presenter';
import {
  PersonalReportPresentationContext,
  PersonalReportRequestContext,
  ReportDatepickerError,
  ReportDatepickerReportView,
  ReportRequestContext
} from './report-datepicker.types';

@Injectable()
export class ReportDatepickerPersonalReportCoordinator {
  private readonly destroy$ = new Subject<void>();
  constructor(
    private reportService: ReportService,
    private departmentLoader: ReportDatepickerDepartmentLoader,
    private presenter: ReportDatepickerReportPresenter
  ) {
  }

  destroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearRowNames(): void {
    this.reportService.clearRowNames();
  }

  resetReports(): void {
    this.reportService.reportsMin = [];
  }

  requestDayOff(view: ReportDatepickerReportView, context: ReportRequestContext): void {
    this.reportService.getReportByUserIdOff(
      context.from, context.to, view.userId).takeUntil(this.destroy$).subscribe(
      () => {
        view.suc = true;
        this.presenter.showDayOffSuccess();
      },
      (error: ReportDatepickerError) => applyReportDatepickerError(view, error)
    );
  }

  requestGroupDayOff(
    view: ReportDatepickerReportView, context: ReportRequestContext
  ): void {
    const from = moment(context.fromDate).format(DATE_FORMAT);
    this.reportService.getReportByGroupIdOff(view.groupId.id, from).takeUntil(this.destroy$).subscribe(
      () => {
        view.suc = true;
        this.presenter.showDayOffSuccess();
      },
      (error: ReportDatepickerError) => applyReportDatepickerError(view, error)
    );
  }

  requestPersonal(
    view: ReportDatepickerReportView, context: ReportRequestContext, department: boolean
  ): void {
    const request: PersonalReportRequestContext = {
      from: context.from, to: context.to,
      fromDate: context.fromDate, department: department,
      freshReportContext: this.reportService.captureFreshReportContext(context.from, context.to, view.userId)
    };
    this.reportService.getReportByUserId(context.from, context.to, view.userId).takeUntil(this.destroy$).subscribe(
      (response: HttpResponse<Object>) => {
        if (response.status === HTTP_STATUS_CODES.OK) {
          this.processPersonalReport(view, response, request);
        }
      },
      (error: ReportDatepickerError) => applyReportDatepickerError(view, error)
    );
  }

  private processPersonalReport(
    view: ReportDatepickerReportView, response: HttpResponse<Object>,
    context: PersonalReportRequestContext
  ): void {
    this.reportService.reportsMin = response.body as Report[];
    this.departmentLoader.load(this.reportService.reportsMin[FIRST_REPORT_INDEX].userID);
    this.reportService.reportsMin.forEach((report: Report) => formatReportFields(report));
    this.prepareDealReports(context.from, context.to);
    this.preparePersonalReport(view, context);
  }

  private preparePersonalReport(
    view: ReportDatepickerReportView, context: PersonalReportRequestContext
  ): void {
    this.reportService.allReports = [];
    this.reportService.filteredReports = [];
    this.reportService.reportDate =
      REPORT_DATE_PREFIX + context.from + REPORT_DATE_SEPARATOR + context.to;
    const presentation: PersonalReportPresentationContext = {
      from: context.from, to: context.to, reportDate: this.reportService.reportDate,
      reports: this.reportService.reportsMin, userId: view.userId,
      department: context.department
    };
    this.reportService.rememberFreshReport(presentation.reports, context.freshReportContext);
    this.presenter.showPersonalReport(presentation);
  }

  private prepareDealReports(from: string, to: string): void {
    const reports = this.reportService.reportsMin as Report[];
    const expectedMonth = dealPayrollMonthFromRange(from, to);
    reports.forEach((report: Report) => {
      const breakdown = report && report.dealPayrollBreakdown;
      if (breakdown && isDealPayrollIdentityMatch(report.userID, expectedMonth, breakdown)) {
        this.reportService.rememberDealPayrollBreakdown(breakdown);
      }
    });
    this.reportService.reportsMin = this.reportService.toCompactReports(reports, expectedMonth);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/modals/report-datepicker-personal-report.coordinator.ts